import { formatPropertyAddress } from '../utils/propertyAddress';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import MlsDisclosure from './MlsDisclosure';

const getAddress = formatPropertyAddress;

const getBathrooms = (property) => {
  const full = Number(property.bathrooms_full || 0);
  const half = Number(property.bathrooms_half || 0);
  return full + (half * 0.5) || null;
};

export default function AreaProperties({ area }) {
  const { language } = useLanguage();
  const isSpanish = language === 'es';
  const [properties, setProperties] = useState([]);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    const controller = new AbortController();
    setStatus('loading');
    fetch('/area-properties.json', { cache: 'no-store', signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Area properties request failed: ${response.status}`);
        return response.json();
      })
      .then((data) => {
        const listings = (data.areas?.[area.slug] || []).slice(0, 9);
        setProperties(listings);
        setStatus(listings.length ? 'ready' : 'empty');
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Error loading area properties:', error);
          setStatus('error');
        }
      });
    return () => controller.abort();
  }, [area.slug]);

  const copy = isSpanish ? {
    kicker: 'Inventario reciente · Selección de lujo',
    title: `Propiedades destacadas en ${area.name}`,
    body: `Nueve propiedades activas de mayor precio, seleccionadas entre los listados más recientes de ${area.city}.`,
    loading: `Cargando propiedades recientes en ${area.name}…`,
    empty: `No hay suficientes propiedades activas en ${area.name} para mostrar en este momento.`,
    beds: 'hab.', baths: 'baños', garage: 'garaje', view: 'Ver detalles', active: 'Activa', search: `Ver todas las propiedades en ${area.name}`
  } : {
    kicker: 'Recent inventory · Elevated selection',
    title: `Featured properties in ${area.name}`,
    body: `Nine higher-priced active properties selected from the newest listings in ${area.city}.`,
    loading: `Loading recent properties in ${area.name}…`,
    empty: `There are not enough active properties in ${area.name} to display right now.`,
    beds: 'beds', baths: 'baths', garage: 'garage', view: 'View details', active: 'Active', search: `See all properties in ${area.name}`
  };

  const formatPrice = (value) => new Intl.NumberFormat(isSpanish ? 'es-US' : 'en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 0
  }).format(Number(value) || 0);

  return (
    <section className="gt-area-properties gt-section" aria-labelledby="gt-area-properties-title">
      <header className="gt-area-properties-heading">
        <div>
          <span className="gt-kicker gt-kicker--dark">{copy.kicker}</span>
          <h2 id="gt-area-properties-title">{copy.title}</h2>
        </div>
        <p>{copy.body}</p>
      </header>

      {status === 'loading' && <p className="gt-area-properties-status" role="status">{copy.loading}</p>}
      {(status === 'empty' || status === 'error') && <p className="gt-area-properties-status" role="status">{copy.empty}</p>}

      {status === 'ready' && (
        <div className="gt-area-property-grid">
          {properties.map((property, index) => {
            const address = getAddress(property);
            const bathrooms = getBathrooms(property);
            return (
              <Link className="gt-area-property-card" to={`/listing/${property.listing_key}`} key={property.listing_key} aria-label={`${copy.view}: ${address}`}>
                <div className="gt-area-property-image">
                  <img src={property.first_photo_url} alt={address} loading={index < 3 ? 'eager' : 'lazy'} />
                  <span>{copy.active}</span>
                </div>
                <div className="gt-area-property-copy">
                  <strong>{formatPrice(property.current_price || property.list_price)}</strong>
                  <h3>{address}</h3>
                  <div>
                    {property.bedrooms_total != null && <span>{property.bedrooms_total} {copy.beds}</span>}
                    {bathrooms != null && <span>{bathrooms} {copy.baths}</span>}
                    {property.garage_spaces != null && <span>{property.garage_spaces} {copy.garage}</span>}
                  </div>
                  <b>{copy.view} <span>↗</span></b>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      <div className="gt-area-properties-footer">
        <Link className="gt-button gt-button--navy" to={`/search?city=${encodeURIComponent(area.city)}&state=${area.state}`}>{copy.search} <span>→</span></Link>
        <div className="gt-area-properties-disclosure">
          <img src={SITE_CONFIG.MLS.LOGO_URL} alt={SITE_CONFIG.MLS.SHORT_NAME} />
          <small><MlsDisclosure variant="preview" showLicenses /></small>
        </div>
      </div>
    </section>
  );
}
